import React, { useState, useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import {
  createConfig,
  decode,
  convertToBase10,
  convertToBase40,
} from "../../../lib/encryption";

const VALID_CHARS = /^[0-9A-Za-z#%*=+]*$/;

interface CypherDecoderProps {
  type?: "green" | "orange";
}

export default function CypherDecoder({ type = "green" }: CypherDecoderProps) {
  const isGreen = type === "green";
  const codeColour = isGreen ? "Green" : "Orange";
  const themeColour = isGreen ? "#3CB44B" : "#F58231";
  const [base40Input, setBase40Input] = useState("");
  const [base10Input, setBase10Input] = useState("");

  const encryptionImport = useSelector(
    (state: RootState) => state.imports.importPreviews?.encryption,
    isEqual,
  );

  // Create config from import preview
  const encryptionConfig = useMemo(() => {
    if (!encryptionImport) return null;
    return createConfig(encryptionImport);
  }, [encryptionImport]);

  // Get channel IDs from config
  const channelIds = useMemo(() => {
    if (!encryptionConfig) return [];
    const channels = encryptionConfig[codeColour].channels;
    return channels.filter((id: string) => id && id.trim() !== "");
  }, [encryptionConfig, codeColour]);

  // Decode and get channel values
  const channelValues = useMemo(() => {
    if (!encryptionConfig || !base40Input) return {};
    const requiredLength = isGreen ? 4 : 5;
    if (base40Input.length !== requiredLength) return {};

    const decoded = decode(codeColour, base40Input, encryptionConfig);
    if (!decoded) return {};

    const map: Record<string, string> = {};
    Object.entries(decoded).forEach(([name, value]) => {
      map[name] = String(value);
    });
    return map;
  }, [encryptionConfig, base40Input, codeColour, isGreen]);

  // Only allow valid encryption code characters
  const handleCodeInput = (e: React.FormEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const filtered = input.value
      .toUpperCase()
      .split("")
      .filter((c) => VALID_CHARS.test(c))
      .join("");
    if (filtered !== input.value) {
      input.value = filtered;
    }
  };

  // Handle base40 input change
  const handleBase40Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setBase40Input(value);

    if (!value) {
      setBase10Input("");
      return;
    }

    const base10 = convertToBase10(value);
    if (base10 !== null) {
      setBase10Input(String(base10));
    } else {
      setBase10Input("");
    }
  };

  // Handle base10 input change
  const handleBase10Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBase10Input(value);

    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      const base40 = convertToBase40(numValue);
      const requiredLength = isGreen ? 4 : 5;
      if (base40 !== null && base40.length <= requiredLength) {
        // Pad to required length
        setBase40Input(base40.padStart(requiredLength, "#"));
      } else {
        setBase40Input("");
      }
    } else {
      setBase40Input("");
    }
  };

  const inputId = isGreen ? "green" : "orange";

  return (
    <div className={styles.container}>
      <span className={styles.title} style={{ color: themeColour }}>
        {isGreen ? "Green Code Cypher" : "Orange Code Cypher"}
      </span>

      <div className={styles.inputs}>
        <div className={styles.inputGroup}>
          <label
            htmlFor={`${inputId}-base40-input`}
            style={{ color: themeColour }}
          >
            Base 40
          </label>
          <input
            id={`${inputId}-base40-input`}
            type="text"
            value={base40Input}
            onChange={handleBase40Change}
            onInput={handleCodeInput}
            className={styles.input}
            style={{ borderColor: themeColour }}
            maxLength={isGreen ? 4 : 5}
            placeholder="Base 40..."
            autoComplete="off"
          />
        </div>

        <div className={styles.inputGroup}>
          <label
            htmlFor={`${inputId}-base10-input`}
            style={{ color: themeColour }}
          >
            Base 10
          </label>
          <input
            id={`${inputId}-base10-input`}
            type="number"
            value={base10Input}
            onChange={handleBase10Change}
            className={styles.input}
            style={{ borderColor: themeColour }}
            min={0}
            placeholder="Base 10..."
            autoComplete="off"
          />
        </div>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            {channelIds.map((id) => (
              <th key={id} style={{ color: themeColour }}>
                {id}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {channelIds.map((id) => (
              <td key={id}>{channelValues[id] ?? "-"}</td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
